import { createSlice, PayloadAction, createAsyncThunk, current } from '@reduxjs/toolkit'
import { SearchParams, SearchResult, SearchResultItem } from '@/types'
import apiService from '@/services/api'

interface SearchState {
  results: SearchResult | null
  lastSearchParams: SearchParams | null
  isLoading: boolean
  error: string | null
  // Infinite scroll: the next page being appended to `results`
  isLoadingMore: boolean
  loadMoreError: string | null
  loadMoreRequestId: string | null
  // Cache key is generated from search params
  cache: Record<
    string,
    {
      results: SearchResult
      timestamp: number
      params: SearchParams
    }
  >
}

const initialState: SearchState = {
  results: null,
  lastSearchParams: null,
  isLoading: false,
  error: null,
  isLoadingMore: false,
  loadMoreError: null,
  loadMoreRequestId: null,
  cache: {},
}

type SearchRootState = { search: SearchState }

// Generate cache key from search parameters. The offset is left out: a search and
// the pages scrolled in after it share one entry
const generateCacheKey = (params: SearchParams): string => {
  // Create a stable string representation of the search parameters
  const sortedParams = Object.keys(params)
    .filter((key) => key !== 'offset')
    .sort()
    .reduce(
      (sorted, key) => {
        const value = params[key as keyof SearchParams]
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            sorted[key] = [...value].sort()
          } else {
            sorted[key] = value
          }
        }
        return sorted
      },
      {} as Record<string, any>
    )

  return btoa(JSON.stringify(sortedParams))
}

// Check if cached results are still valid (5 minutes)
const isCacheValid = (timestamp: number): boolean => {
  const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
  return Date.now() - timestamp < CACHE_DURATION
}

const resultKey = (result: SearchResultItem) => `${result.file_id}-${result.chunk_index}`

// The store already shows this exact search and its cache entry is still fresh
const isCurrentSearch = (state: SearchState, params: SearchParams): boolean => {
  if (!state.results || !state.lastSearchParams || state.isLoading) return false
  const cacheKey = generateCacheKey(params)
  const cached = state.cache[cacheKey]
  return (
    generateCacheKey(state.lastSearchParams) === cacheKey &&
    !!cached &&
    isCacheValid(cached.timestamp)
  )
}

// Async thunk for performing search
export const performSearch = createAsyncThunk(
  'search/performSearch',
  async (params: SearchParams, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { search: SearchState }
      const cacheKey = generateCacheKey(params)

      // Check if we have valid cached results
      const cached = state.search.cache[cacheKey]
      if (cached && isCacheValid(cached.timestamp)) {
        return { results: cached.results, fromCache: true, cacheKey }
      }

      // Perform the search
      const results = await apiService.searchDocuments(params)
      return { results, fromCache: false, cacheKey }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Search failed')
    }
  },
  {
    // Coming back to the search page (e.g. Back from a preview) keeps the pages
    // already scrolled in instead of starting over, and a repeat of a search that's
    // still loading (double Enter, StrictMode's double effect) is dropped
    condition: (params, { getState }) => {
      const state = (getState() as SearchRootState).search
      const alreadyLoading =
        state.isLoading &&
        !!state.lastSearchParams &&
        generateCacheKey(state.lastSearchParams) === generateCacheKey(params)
      return !alreadyLoading && !isCurrentSearch(state, params)
    },
  }
)

// Async thunk for appending the next page of the current search
export const loadMoreSearchResults = createAsyncThunk<
  SearchResult,
  void,
  { state: SearchRootState; rejectValue: string }
>(
  'search/loadMoreSearchResults',
  async (_, { getState, rejectWithValue }) => {
    const { results, lastSearchParams } = getState().search
    try {
      return await apiService.searchDocuments({
        ...lastSearchParams!,
        offset: results!.next_offset!,
      })
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Search failed')
    }
  },
  {
    condition: (_, { getState }) => {
      const { results, lastSearchParams, isLoading, isLoadingMore } = getState().search
      return (
        !!results &&
        !!lastSearchParams &&
        results.next_offset != null &&
        !isLoading &&
        !isLoadingMore
      )
    },
  }
)

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.results = null
      state.lastSearchParams = null
      state.error = null
      state.isLoadingMore = false
      state.loadMoreError = null
      state.loadMoreRequestId = null
    },
    clearSearchCache: (state) => {
      state.cache = {}
    },
    // Manual cache cleanup - remove entries older than cache duration
    cleanupCache: (state) => {
      const now = Date.now()
      Object.keys(state.cache).forEach((key) => {
        if (!isCacheValid(state.cache[key].timestamp)) {
          delete state.cache[key]
        }
      })
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(performSearch.pending, (state, action) => {
        state.isLoading = true
        state.error = null
        state.lastSearchParams = action.meta.arg
        // A page still loading for the previous search gets dropped when it lands
        state.isLoadingMore = false
        state.loadMoreError = null
        state.loadMoreRequestId = null
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        state.isLoading = false
        state.results = action.payload.results
        state.error = null

        // Cache the results if they're not from cache
        if (!action.payload.fromCache) {
          state.cache[action.payload.cacheKey] = {
            results: action.payload.results,
            timestamp: Date.now(),
            params: state.lastSearchParams!,
          }
        }
      })
      .addCase(performSearch.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(loadMoreSearchResults.pending, (state, action) => {
        state.isLoadingMore = true
        state.loadMoreError = null
        state.loadMoreRequestId = action.meta.requestId
      })
      .addCase(loadMoreSearchResults.fulfilled, (state, action) => {
        if (state.loadMoreRequestId !== action.meta.requestId) return
        state.isLoadingMore = false
        state.loadMoreRequestId = null
        if (!state.results || !state.lastSearchParams) return

        const page = action.payload
        const loaded = current(state.results)
        const seen = new Set(loaded.results.map(resultKey))
        const results = [...loaded.results, ...page.results.filter((r) => !seen.has(resultKey(r)))]
        const merged: SearchResult = {
          ...loaded,
          results,
          returned_results: results.length,
          total_results: page.total_results,
          next_offset: page.next_offset,
        }
        state.results = merged

        // Keep the cache entry in step so coming back restores every loaded page
        const cached = state.cache[generateCacheKey(state.lastSearchParams)]
        if (cached) {
          cached.results = merged
        }
      })
      .addCase(loadMoreSearchResults.rejected, (state, action) => {
        if (state.loadMoreRequestId !== action.meta.requestId) return
        state.isLoadingMore = false
        state.loadMoreRequestId = null
        state.loadMoreError = action.payload ?? action.error.message ?? 'Search failed'
      })
  },
})

export const { clearSearchResults, clearSearchCache, cleanupCache, setError } = searchSlice.actions

export default searchSlice.reducer

// Selectors
export const selectSearchResults = (state: { search: SearchState }) => state.search.results
export const selectSearchLoading = (state: { search: SearchState }) => state.search.isLoading
export const selectSearchError = (state: { search: SearchState }) => state.search.error
export const selectSearchLoadingMore = (state: { search: SearchState }) =>
  state.search.isLoadingMore
export const selectSearchLoadMoreError = (state: { search: SearchState }) =>
  state.search.loadMoreError
export const selectLastSearchParams = (state: { search: SearchState }) =>
  state.search.lastSearchParams
export const selectCacheSize = (state: { search: SearchState }) =>
  Object.keys(state.search.cache).length

// The store already holds this exact search (with its loaded pages)
export const selectIsCurrentSearch = (state: { search: SearchState }, params: SearchParams) =>
  isCurrentSearch(state.search, params)

// Check if we have cached results for given parameters
export const selectHasCachedResults = (
  state: { search: SearchState },
  params: SearchParams
): boolean => {
  const cacheKey = generateCacheKey(params)
  const cached = state.search.cache[cacheKey]
  return cached ? isCacheValid(cached.timestamp) : false
}
